import { useSelector } from "react-redux";

// Original hook - returns entire game object (causes performance issues)
export default function useGame(tempTableId) {
  try {
    const gameTableData = useSelector(
      (state) => state.gameTable.games[tempTableId]
    );

    if (!gameTableData) {
      console.warn(`[useGame] No game data found for tableId: ${tempTableId}`);
      return {};
    }

    return gameTableData;
  } catch (error) {
    console.error(`[useGame] Error accessing game data for tableId: ${tempTableId}:`, error);
    return {};
  }
}

// Optimized hooks for specific values - prevents unnecessary re-renders
export function useGameValue(tempTableId, selector) {
  return useSelector((state) => {
    try {
      const gameData = state.gameTable.games[tempTableId];
      if (!gameData) {
        console.warn(`[useGameValue] No game data found for tableId: ${tempTableId}`);
        return undefined;
      }
      return selector(gameData);
    } catch (error) {
      console.error(`[useGameValue] Error accessing game data for tableId: ${tempTableId}:`, error);
      return undefined;
    }
  });
}

// Specific hooks for commonly used values
export function useGameDefinition(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.gameDefinition);
  } catch (error) {
    console.error(`[useGameDefinition] Error accessing gameDefinition for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useGameStage(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.gameStage);
  } catch (error) {
    console.error(`[useGameStage] Error accessing gameStage for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function usePlayerSeat(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.playerSeat);
  } catch (error) {
    console.error(`[usePlayerSeat] Error accessing playerSeat for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function usePlayerData(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.playerData);
  } catch (error) {
    console.error(`[usePlayerData] Error accessing playerData for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useCommunityCards(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.communityCards);
  } catch (error) {
    console.error(`[useCommunityCards] Error accessing communityCards for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useClientCards(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.playerCards);
  } catch (error) {
    console.error(`[useClientCards] Error accessing playerCards for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useTotalPotAmount(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.totalPotAmt);
  } catch (error) {
    console.error(`[useTotalPotAmount] Error accessing totalPotAmt for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useMultiPotAmounts(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.multiPotAmounts);
  } catch (error) {
    console.error(`[useMultiPotAmounts] Error accessing multiPotAmounts for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useGameConnectionStatus(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.gameConnectionStatus);
  } catch (error) {
    console.error(`[useGameConnectionStatus] Error accessing gameConnectionStatus for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useGameTableDimensions(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.gameTableDimensions);
  } catch (error) {
    console.error(`[useGameTableDimensions] Error accessing gameTableDimensions for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useShowCommunityCardsImmediately(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.showCommunityCardsImmediately);
  } catch (error) {
    console.error(`[useShowCommunityCardsImmediately] Error accessing showCommunityCardsImmediately for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useIsTourneyGame(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.isTourneyGame);
  } catch (error) {
    console.error(`[useIsTourneyGame] Error accessing isTourneyGame for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useGameEnded(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.gameEnded);
  } catch (error) {
    console.error(`[useGameEnded] Error accessing gameEnded for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useTourneyGameCompleted(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.tourneyGameCompleted);
  } catch (error) {
    console.error(`[useTourneyGameCompleted] Error accessing tourneyGameCompleted for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useSitOutOption(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.sitOutOption);
  } catch (error) {
    console.error(`[useSitOutOption] Error accessing sitOutOption for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function usePlayerSitOutTimer(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.playerSitOutTimer);
  } catch (error) {
    console.error(`[usePlayerSitOutTimer] Error accessing playerSitOutTimer for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useShowAllCards(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.showAllCards);
  } catch (error) {
    console.error(`[useShowAllCards] Error accessing showAllCards for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useShownCards(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.shownCards);
  } catch (error) {
    console.error(`[useShownCards] Error accessing shownCards for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useGameId(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.gameId);
  } catch (error) {
    console.error(`[useGameId] Error accessing gameId for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useIsWinnerDeclared(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.isWinnerDeclared);
  } catch (error) {
    console.error(`[useIsWinnerDeclared] Error accessing isWinnerDeclared for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useWinningPotDisplay(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.winningPotDisplay);
  } catch (error) {
    console.error(`[useWinningPotDisplay] Error accessing winningPotDisplay for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useIsUserWinner(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.isUserWinner);
  } catch (error) {
    console.error(`[useIsUserWinner] Error accessing isUserWinner for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useMinRaiseAmount(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.minRaiseAmount);
  } catch (error) {
    console.error(`[useMinRaiseAmount] Error accessing minRaiseAmount for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useMaxRaiseAmount(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.maxRaiseAmount);
  } catch (error) {
    console.error(`[useMaxRaiseAmount] Error accessing maxRaiseAmount for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useAtTableAmount(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.atTableAmount);
  } catch (error) {
    console.error(`[useAtTableAmount] Error accessing atTableAmount for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useBuyInTimeInSeconds(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.buyInTimeInSeconds);
  } catch (error) {
    console.error(`[useBuyInTimeInSeconds] Error accessing buyInTimeInSeconds for tableId: ${tempTableId}:`, error);
    return undefined;
    }
}

export function useAntiBanking(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.antiBanking);
  } catch (error) {
    console.error(`[useAntiBanking] Error accessing antiBanking for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useShowInsufficientBalance(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.showInsufficientBalance);
  } catch (error) {
    console.error(`[useShowInsufficientBalance] Error accessing showInsufficientBalance for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useOpenBuyInModal(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.openBuyInModal);
  } catch (error) {
    console.error(`[useOpenBuyInModal] Error accessing openBuyInModal for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useOpenRebuyInModal(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.openRebuyInModal);
  } catch (error) {
    console.error(`[useOpenRebuyInModal] Error accessing openRebuyInModal for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useIsGameStarted(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.isGameStarted);
  } catch (error) {
    console.error(`[useIsGameStarted] Error accessing isGameStarted for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useOpenTourneyWinnerModal(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.openTourneyWinnerModal);
  } catch (error) {
    console.error(`[useOpenTourneyWinnerModal] Error accessing openTourneyWinnerModal for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useTourneyPrizeDetails(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.tourneyPrizeDetails);
  } catch (error) {
    console.error(`[useTourneyPrizeDetails] Error accessing tourneyPrizeDetails for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useOpenTourneyEliminationModal(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.openTourneyEliminationModal);
  } catch (error) {
    console.error(`[useOpenTourneyEliminationModal] Error accessing openTourneyEliminationModal for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useTourneyEliminationDetails(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.tourneyEliminationDetails);
  } catch (error) {
    console.error(`[useTourneyEliminationDetails] Error accessing tourneyEliminationDetails for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useTourneyRebuyDetails(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.tourneyRebuyDetails);
  } catch (error) {
    console.error(`[useTourneyRebuyDetails] Error accessing tourneyRebuyDetails for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useDealObject(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.dealObject);
  } catch (error) {
    console.error(`[useDealObject] Error accessing dealObject for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useDealICMObject(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.dealICMObject);
  } catch (error) {
    console.error(`[useDealICMObject] Error accessing dealICMObject for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useWatchTable(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.watchTable);
  } catch (error) {
    console.error(`[useWatchTable] Error accessing watchTable for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useRITRequestEndTime(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.RITRequestEndTime);
  } catch (error) {
    console.error(`[useRITRequestEndTime] Error accessing RITRequestEndTime for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useTourneyId(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.tourneyId);
  } catch (error) {
    console.error(`[useTourneyId] Error accessing tourneyId for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useIsWaitingForBigBlind(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.isWaitingForBigBlind);
  } catch (error) {
    console.error(`[useIsWaitingForBigBlind] Error accessing isWaitingForBigBlind for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useGameType(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.gameType);
  } catch (error) {
    console.error(`[useGameType] Error accessing gameType for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useShowFold(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.showFold);
  } catch (error) {
    console.error(`[useShowFold] Error accessing showFold for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useShowCheck(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.showCheck);
  } catch (error) {
    console.error(`[useShowCheck] Error accessing showCheck for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useShowCall(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.showCall);
  } catch (error) {
    console.error(`[useShowCall] Error accessing showCall for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useShowRaise(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.showRaise);
  } catch (error) {
    console.error(`[useShowRaise] Error accessing showRaise for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useShowAllIn(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.showAllIn);
  } catch (error) {
    console.error(`[useShowAllIn] Error accessing showAllIn for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useCallAmount(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.callAmount);
  } catch (error) {
    console.error(`[useCallAmount] Error accessing callAmount for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useRaiseAmount(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.raiseAmount);
  } catch (error) {
    console.error(`[useRaiseAmount] Error accessing raiseAmount for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useShowDefaultActions(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.showDefaultActions);
  } catch (error) {
    console.error(`[useShowDefaultActions] Error accessing showDefaultActions for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useRITRequest(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.RITRequest);
  } catch (error) {
    console.error(`[useRITRequest] Error accessing RITRequest for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useShowAutoActions(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.showAutoActions);
  } catch (error) {
    console.error(`[useShowAutoActions] Error accessing showAutoActions for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useAutoCall(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.autoCall);
  } catch (error) {
    console.error(`[useAutoCall] Error accessing autoCall for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useAutoCallAny(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.autoCallAny);
  } catch (error) {
    console.error(`[useAutoCallAny] Error accessing autoCallAny for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useAutoFold(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.autoFold);
  } catch (error) {
    console.error(`[useAutoFold] Error accessing autoFold for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useAutoCheck(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.autoCheck);
  } catch (error) {
    console.error(`[useAutoCheck] Error accessing autoCheck for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useAutoCheckOrFold(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.autoCheckOrFold);
  } catch (error) {
    console.error(`[useAutoCheckOrFold] Error accessing autoCheckOrFold for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useAutoAction(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.autoAction);
  } catch (error) {
    console.error(`[useAutoAction] Error accessing autoAction for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useThrowables(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.throwables);
  } catch (error) {
    console.error(`[useThrowables] Error accessing throwables for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useShowMaintenancePopup(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.showMaintenancePopup);
  } catch (error) {
    console.error(`[useShowMaintenancePopup] Error accessing showMaintenancePopup for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useTourneyNextLevelUpDetails(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.tourneyNextLevelUpDetails);
  } catch (error) {
    console.error(`[useTourneyNextLevelUpDetails] Error accessing tourneyNextLevelUpDetails for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useTourneyRankingLeaderboardDetails(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.tourneyRankingLeaderboardDetails);
  } catch (error) {
    console.error(`[useTourneyRankingLeaderboardDetails] Error accessing tourneyRankingLeaderboardDetails for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useIsTourneyRankingLeaderboardRequestInProgress(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.isTourneyRankingLeaderboardRequestInProgress);
  } catch (error) {
    console.error(`[useIsTourneyRankingLeaderboardRequestInProgress] Error accessing isTourneyRankingLeaderboardRequestInProgress for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useGameModal(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.gameModal);
  } catch (error) {
    console.error(`[useGameModal] Error accessing gameModal for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useToastMessages(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.toastMessages);
  } catch (error) {
    console.error(`[useToastMessages] Error accessing toastMessages for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useRITCommunityCards(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.RITCommunityCards);
  } catch (error) {
    console.error(`[useRITCommunityCards] Error accessing RITCommunityCards for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useShowDealICMPayoutDistribution(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.showDealICMPayoutDistribution);
  } catch (error) {
    console.error(`[useShowDealICMPayoutDistribution] Error accessing showDealICMPayoutDistribution for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useShowDealConclusionPopup(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.showDealConclusionPopup);
  } catch (error) {
    console.error(`[useShowDealConclusionPopup] Error accessing showDealConclusionPopup for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useTourneyAutoPlay(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.tourneyAutoPlay);
  } catch (error) {
    console.error(`[useTourneyAutoPlay] Error accessing tourneyAutoPlay for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useTableId(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.tableId);
  } catch (error) {
    console.error(`[useTableId] Error accessing tableId for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useSetBuyInValueForPlayerPosition(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.setBuyInValueForPlayerPosition);
  } catch (error) {
    console.error(`[useSetBuyInValueForPlayerPosition] Error accessing setBuyInValueForPlayerPosition for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useIsOptForAutoReBuy(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.isOptForAutoReBuy);
  } catch (error) {
    console.error(`[useIsOptForAutoReBuy] Error accessing isOptForAutoReBuy for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function usePlayerTurn(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.playerTurn);
  } catch (error) {
    console.error(`[usePlayerTurn] Error accessing playerTurn for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useClickedFavToggle(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.clickedFavToggle);
  } catch (error) {
    console.error(`[useClickedFavToggle] Error accessing clickedFavToggle for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}

export function useTourneyPlayerRankDetails(tempTableId) {
  try {
    return useGameValue(tempTableId, (game) => game.tourneyPlayerRankDetails);
  } catch (error) {
    console.error(`[useTourneyPlayerRankDetails] Error accessing tourneyPlayerRankDetails for tableId: ${tempTableId}:`, error);
    return undefined;
  }
}
